import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import { Textarea } from '@/components/ui/textarea';
import { useCreateAppointment, useDryRunAppointment } from '@/hooks/data/useAppointments';
import { useClients } from '@/hooks/data/useClients';
import { useProperties } from '@/hooks/data/useProperties';
import { useAgents } from '@/hooks/data/useAgents';
import { useAuth } from '@/hooks/useAuth';
import { formatDate, formatTime } from '@/lib/formatLocale';
import { trafficLightBlockMessage } from '@/lib/trafficLightBlock';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultType?: 'aufnahme' | 'besichtigung' | 'notar';
  defaultClientId?: string;
  defaultPropertyId?: string;
  /**
   * Pre-selected, locked client. When set, the client picker is replaced
   * by a read-only chip and `clientId` is fixed to this id. Used when
   * opening from a client's detail view — it works even for clients the
   * audience-filtered picker would NOT list (e.g. imported Abgeber leads),
   * because the id is passed directly and never looked up in the picker.
   */
  lockedClient?: {
    id: string;
    label: string;
    secondary?: string;
    address?: string | null;
    location?: string | null;
  };
}

export function CreateAppointmentDialog({
  open,
  onOpenChange,
  defaultType,
  defaultClientId,
  defaultPropertyId,
  lockedClient,
}: Props) {
  const { t } = useTranslation();
  const create = useCreateAppointment();
  const dryRun = useDryRunAppointment();
  const { profile } = useAuth();
  // Every authenticated user can pick the target agent. Earlier
  // versions locked makers to themselves so they wouldn't accidentally
  // dump an appointment on a colleague, but operators report that
  // real-world workflow needs full flexibility (CC books for makler X,
  // makler covers for colleague Y, admin oversees everyone).
  const canPickAgent = true;
  const { data: agents = [] } = useAgents();
  // Maklers default to themselves, call-center to their
  // cc_default_makler_id, admin starts empty. All three can pivot.
  const [agentProfileId, setAgentProfileId] = useState<string>(() => {
    if (profile?.role === 'makler') return profile?.id ?? '';
    if (profile?.role === 'call_center')
      return (profile as { cc_default_makler_id?: string | null })?.cc_default_makler_id ?? '';
    return '';
  });

  const [type, setType] = useState<'aufnahme' | 'besichtigung' | 'kaufanbot' | 'notar' | 'sonstiges'>(
    defaultType ?? 'aufnahme',
  );
  const [clientId, setClientId] = useState(lockedClient?.id ?? defaultClientId ?? '');
  const [propertyId, setPropertyId] = useState(defaultPropertyId ?? '');
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setHours(10, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return toLocalInput(d);
  });
  const [notes, setNotes] = useState('');
  const [retroactive, setRetroactive] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Filter clients by appointment type:
  //   aufnahme = sellers, besichtigung = buyers, notar = both (no filter)
  // Bump limit so the SearchablePicker (client-side filter) sees the
  // entire dataset instead of just the first 100 rows the API default
  // returns. ~9 200 clients + ~885 properties — both fit comfortably
  // in a single round-trip and TanStack Query caches the result.
  const clientFilter = useMemo(() => {
    if (type === 'aufnahme') return { clientType: 'prodavac' as const, limit: 10000 };
    if (type === 'besichtigung') return { clientType: 'kupac' as const, limit: 10000 };
    return { limit: 10000 };
  }, [type]);
  const { data: clients = [] } = useClients(clientFilter);

  // Properties:
  //   aufnahme  → all non-archived (the property may not exist yet at
  //               intake time; pre-fix filtered to "lead" only which
  //               returned 0 hits for any new client + blocked the CC
  //               from searching by address)
  //   besichtigung → active
  //   notar     → kaufanbot_angenommen
  // limit=5000 ensures the SearchablePicker sees the whole dataset
  // (was hitting API default of 100 → "Kölgengasse" was outside the
  // first 100 sorted by createdAt desc, hence Marianne's "Keine
  // Treffer" when searching "Kö").
  const propsFilter = useMemo(() => {
    if (type === 'besichtigung') return { marketingStage: 'active' as const, limit: 5000 };
    if (type === 'notar') return { marketingStage: 'kaufanbot_angenommen' as const, limit: 5000 };
    return { limit: 5000 }; // aufnahme + sonstiges + kaufanbot
  }, [type]);
  const { data: properties = [] } = useProperties(propsFilter);

  // Selected client object for the address hint below the property picker
  // — Marianne reported the address used to surface here; surfacing the
  // client's known address makes it easier to (a) recognise if the
  // property is already in the CRM, (b) create one at the maker visit.
  const selectedClient = useMemo(
    () =>
      lockedClient
        ? { address: lockedClient.address ?? null, location: lockedClient.location ?? null }
        : clientId
          ? clients.find((c) => c.id === clientId) ?? null
          : null,
    [clientId, clients, lockedClient],
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!clientId) {
      setErr(t('appointments.create.errors.client_required', 'Klient erforderlich'));
      return;
    }
    if (canPickAgent && !agentProfileId) {
      setErr(t('appointments.create.errors.agent_required', 'Bitte einen Makler auswählen'));
      return;
    }
    const ts = new Date(date).getTime();
    if (Number.isNaN(ts)) {
      setErr(t('appointments.create.errors.date_past', 'Datum darf nicht in der Vergangenheit liegen'));
      return;
    }
    // Past-date guard only applies when this is NOT a retroactive entry.
    // Retroactive rows backfill paper-era history and are created already
    // in 'completed' status by the backend.
    if (!retroactive && ts < Date.now() - 60_000) {
      setErr(t('appointments.create.errors.date_past', 'Datum darf nicht in der Vergangenheit liegen'));
      return;
    }

    const payload = {
      clientId,
      propertyId: propertyId || null,
      appointmentType: type,
      appointmentDate: ts,
      notes: notes.trim() || null,
      agentProfileId: agentProfileId || null,
      retroactive: retroactive || undefined,
    };

    // Pre-flight: lock + same-day duplicate check. The backend runs the
    // same guards on the real submit, but checking up front lets us
    // present a clean confirm dialog without first writing.
    try {
      const dry = await dryRun.mutateAsync(payload);

      // Batch AM — property is already notariell verkauft. Non-admin
      // hard stop; admin sees a confirm + sends overridePropertySold.
      if (dry.property_sold) {
        if (profile?.role !== 'admin') {
          setErr(
            t(
              'appointments.create.errors.property_sold',
              'Diese Immobilie ist bereits notariell verkauft — keine neuen Termine mehr möglich.',
            ),
          );
          return;
        }
        const okSold = window.confirm(
          t(
            'appointments.create.confirm.override_property_sold',
            '⚠ Diese Immobilie ist bereits notariell verkauft.\n\nAls Admin können Sie den Termin trotzdem anlegen — die Aktion wird protokolliert.\n\nTermin trotzdem anlegen?',
          ),
        );
        if (!okSold) return;
      }

      // Batch BE: closed seller (sold elsewhere / withdrew) → hard stop for
      // EVERYONE incl. admin (reopen first via the client record).
      if (dry.client_closed) {
        setErr(
          t(
            'appointments.create.errors.client_closed',
            'Dieser Verkäufer ist geschlossen (verkauft/zurückgezogen) — keine neuen Termine möglich.',
          ),
        );
        return;
      }

      // Preuzmi-klijenta: contract-signed seller exclusive to another
      // makler → non-admin hard stop; admin confirm + override (reuses the
      // overrideAufnahmeLock plumbing, audited server-side).
      if (dry.contract_locked) {
        const holderName = dry.contract_locked.assigned_agent_name ?? '—';
        if (profile?.role !== 'admin') {
          setErr(
            t('appointments.create.errors.contract_locked', {
              defaultValue:
                'Vertrag unterschrieben — dieser Klient ist exklusiv bei {{agent}}. Keine Termine durch andere Makler möglich.',
              agent: holderName,
            }),
          );
          return;
        }
        const okContract = window.confirm(
          t('appointments.create.confirm.override_contract_locked', {
            defaultValue:
              '⚠ Vertrag unterschrieben — Klient ist exklusiv bei {{agent}}.\n\nAls Admin können Sie den Termin trotzdem anlegen — die Aktion wird protokolliert.\n\nTermin trotzdem anlegen?',
            agent: holderName,
          }),
        );
        if (!okContract) return;
      }

      // Aufnahme lock → only admin can override. Non-admin: hard stop.
      if (dry.aufnahme_lock) {
        const lockDate = formatDate(new Date(dry.aufnahme_lock.lock_until));
        const lockAgent = dry.aufnahme_lock.lock_agent_name ?? '—';
        if (profile?.role !== 'admin') {
          setErr(
            t(
              'appointments.create.errors.aufnahme_locked',
              `Klient ist bis ${lockDate} für ${lockAgent} gesperrt (30-Tage Aufnahme-Sperre). Bitte Admin kontaktieren.`,
              { date: lockDate, agent: lockAgent },
            ),
          );
          return;
        }
        const okOverride = window.confirm(
          t(
            'appointments.create.confirm.override_aufnahme_lock',
            `⚠ Klient ist bis ${lockDate} für ${lockAgent} gesperrt (Aufnahme-Sperre).\n\nAls Admin können Sie diese Sperre überschreiben — die Aktion wird protokolliert.\n\nTermin trotzdem anlegen?`,
            { date: lockDate, agent: lockAgent },
          ),
        );
        if (!okOverride) return;
      }

      // Traffic-light gate → hard block for non-admin if existing case
      // is OFF/YELLOW/GREEN. Admin gets a confirm to override.
      if (dry.traffic_light_block) {
        if (profile?.role !== 'admin') {
          // Names the blocking appointment and says what ends THIS light's
          // block — one sentence for all three used to promise „nach Ablauf
          // oder Absage" for a signed contract, where neither ever happens.
          setErr(trafficLightBlockMessage(dry.traffic_light_block, t));
          return;
        }
        const state = dry.traffic_light_block.existing_light;
        const stateLabel =
          state === 'off'
            ? t('traffic_light.off', 'Termin geplant')
            : state === 'yellow'
              ? t('traffic_light.yellow', 'Reservierung läuft')
              : t('traffic_light.green', 'Auftrag bekommen');
        const okOverride = window.confirm(
          t('appointments.create.confirm.override_traffic_light', {
            defaultValue:
              '⚠ Bestehender Fall ist {{state}}. Als Admin können Sie das überschreiben — die Aktion wird protokolliert.\n\nTrotzdem anlegen?',
            state: stateLabel,
          }),
        );
        if (!okOverride) return;
      }

      // Same-day duplicate → soft warning regardless of role.
      if (dry.conflicts.length > 0) {
        const lines = dry.conflicts
          .map((c) => {
            const t1 = formatTime(new Date(c.appointment_date), {
              hour: '2-digit',
              minute: '2-digit',
            });
            return `• ${t1} — ${c.agent_name ?? '—'} (${c.appointment_type})`;
          })
          .join('\n');
        const okDup = window.confirm(
          t(
            'appointments.create.confirm.duplicate_same_day',
            `Klient hat bereits am selben Tag ${dry.conflicts.length} Termin(e):\n\n${lines}\n\nTrotzdem anlegen?`,
            { count: dry.conflicts.length, lines },
          ),
        );
        if (!okDup) return;
      }

      // Real submit (with admin override flags if applicable).
      await create.mutateAsync({
        ...payload,
        overrideAufnahmeLock:
          profile?.role === 'admin' && (dry.aufnahme_lock != null || dry.contract_locked != null)
            ? true
            : undefined,
        overridePropertySold:
          profile?.role === 'admin' && dry.property_sold != null ? true : undefined,
      });
      onOpenChange(false);
      setNotes('');
      setClientId('');
      setPropertyId('');
    } catch (cause) {
      setErr((cause as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('appointments.create.title', 'Neuen Termin anlegen')}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <Label>{t('appointments.fields.type', 'Typ')} *</Label>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value as 'aufnahme' | 'besichtigung' | 'kaufanbot' | 'notar' | 'sonstiges')}
              required
            >
              <option value="aufnahme">{t('enums.appointment_type.aufnahme', 'Aufnahme')}</option>
              <option value="besichtigung">{t('enums.appointment_type.besichtigung', 'Besichtigung')}</option>
              <option value="kaufanbot">{t('enums.appointment_type.kaufanbot', 'Kaufanbot')}</option>
              <option value="notar">{t('enums.appointment_type.notar', 'Notar-Termin')}</option>
              <option value="sonstiges">{t('enums.appointment_type.sonstiges', 'Sonstiges')}</option>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>{t('appointments.fields.client', 'Klient')} *</Label>
            {lockedClient ? (
              <div className="flex items-center gap-2 rounded-md border border-line bg-paper-2 px-3 py-2 text-sm">
                <UserCircle2 className="h-4 w-4 flex-shrink-0 text-ink-3" />
                <span className="font-medium text-ink">{lockedClient.label}</span>
                {lockedClient.secondary && (
                  <span className="ms-auto font-mono text-xs text-ink-3">
                    {lockedClient.secondary}
                  </span>
                )}
              </div>
            ) : (
              <SearchablePicker
                value={clientId}
                onChange={setClientId}
                placeholder={t('appointments.fields.client_pick', 'Klient suchen — Name oder Telefon …')}
                emptyText={t('common.no_matches', 'Keine Treffer')}
                required
                options={clients.map((c) => ({
                  value: c.id,
                  label:
                    [c.title, c.first_name, c.last_name].filter(Boolean).join(' ') || '—',
                  secondary: c.phone ?? undefined,
                }))}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('appointments.fields.property', 'Immobilie')}</Label>
            <SearchablePicker
              value={propertyId}
              onChange={setPropertyId}
              placeholder={t('appointments.fields.property_pick', 'Immobilie suchen — Adresse oder Stadt …')}
              emptyText={t('common.no_matches', 'Keine Treffer')}
              options={properties.map((p) => ({
                value: p.id,
                label:
                  (p.address ?? `${p.street ?? ''} ${p.street_number ?? ''}`.trim()) || '—',
                secondary: p.city ?? undefined,
              }))}
            />
            {/* Show the selected client's address as a hint so the CC user
                can either (a) recognise that the property is already in
                the CRM and pick it, or (b) note the address for the
                maker visit if it isn't yet entered. Pre-fix Marianne
                reported the address didn't surface anywhere. */}
            {selectedClient && (selectedClient.address || selectedClient.location) && (
              <p className="text-[11px] text-ink-3 leading-snug">
                {t('appointments.fields.client_address_hint', 'Adresse des Klienten: ')}
                <span className="text-ink-2 font-medium">
                  {[selectedClient.address, selectedClient.location].filter(Boolean).join(' · ')}
                </span>
                {type === 'aufnahme' && (
                  <>
                    {' — '}
                    {t(
                      'appointments.fields.aufnahme_property_hint',
                      'die Immobilie wird beim Termin vor Ort vom Makler angelegt.',
                    )}
                  </>
                )}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('appointments.fields.agent', 'Zuständiger Makler')}</Label>
            {canPickAgent ? (
              <>
                <SearchablePicker
                  value={agentProfileId}
                  onChange={setAgentProfileId}
                  placeholder={t('appointments.fields.agent_pick', 'Makler wählen …')}
                  emptyText={t('common.no_matches', 'Keine Treffer')}
                  options={agents
                    .filter((a) => a.status === 'active' && a.role === 'makler')
                    .map((a) => ({
                      value: a.id,
                      label: a.full_name,
                      secondary: a.agent_id,
                    }))}
                />
                {profile?.role === 'call_center' &&
                  (() => {
                    const ccDefault = (profile as { cc_default_makler_id?: string | null })
                      .cc_default_makler_id;
                    if (!ccDefault) return null;
                    const defaultAgent = agents.find((a) => a.id === ccDefault);
                    if (!defaultAgent) return null;
                    const isPivot = agentProfileId && agentProfileId !== ccDefault;
                    if (isPivot) {
                      const target = agents.find((a) => a.id === agentProfileId);
                      return (
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          {t(
                            'appointments.fields.cc_pivot_warning',
                            `⚠ Sie legen gerade einen Termin für ${target?.full_name ?? '—'} an. Ihr Standard-Makler ist ${defaultAgent.full_name}.`,
                            {
                              target: target?.full_name ?? '—',
                              default: defaultAgent.full_name,
                            },
                          )}
                        </p>
                      );
                    }
                    return (
                      <p className="text-xs text-ink-3">
                        {t(
                          'appointments.fields.cc_default_info',
                          `Ihr Standard-Makler: ${defaultAgent.full_name}`,
                          { name: defaultAgent.full_name },
                        )}
                      </p>
                    );
                  })()}
              </>
            ) : (
              <div className="flex items-center gap-2 rounded-md border border-line bg-paper-2 px-3 py-2 text-sm">
                <UserCircle2 className="h-4 w-4 text-ink-3" />
                <span className="text-ink-3">
                  {t('appointments.fields.assigned_to', 'Wird Ihnen zugewiesen:')}
                </span>
                <span className="font-medium text-ink">{profile?.full_name}</span>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('appointments.fields.date', 'Datum & Zeit')} *</Label>
            <Input
              type="datetime-local"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                // If the user picks a future date, automatically untick
                // "Rückwirkend" — otherwise the checkbox stays stuck on
                // "true" from a previous past-date entry and silently
                // suppresses SMS + email on the next create.
                const ts = new Date(e.target.value).getTime();
                if (!Number.isNaN(ts) && ts >= Date.now() && retroactive) {
                  setRetroactive(false);
                }
              }}
              required
            />
            {/* "Rückwirkend" only makes sense for past appointments;
                surfacing it for future dates was the trap behind Lejla's
                missing-SMS report (Kristina ticked it by accident → backend
                treated the row as a paper-era backfill → SMS suppressed). */}
            {date && new Date(date).getTime() < Date.now() - 60_000 && (
              <label className="flex items-center gap-2 text-xs text-ink-3 mt-1">
                <input
                  type="checkbox"
                  checked={retroactive}
                  onChange={(e) => setRetroactive(e.target.checked)}
                  className="h-3.5 w-3.5 accent-[hsl(var(--brand-gold))]"
                />
                <span>
                  {t(
                    'appointments.fields.retroactive',
                    'Rückwirkend (Termin aus der Vergangenheit nachtragen — wird als „erledigt" gespeichert, keine SMS/E-Mail-Benachrichtigung)',
                  )}
                </span>
              </label>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('appointments.fields.notes', 'Notizen')}</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {err && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {err}
            </p>
          )}

          {/* SMS pre-flight preview — surface the exact text the client
              will receive, so a typo in date/time is visible BEFORE the
              SMS goes out. Renders only when date + client + agent are
              all known (otherwise the message would be a placeholder). */}
          {date && clientId && (() => {
            const ts = new Date(date);
            if (isNaN(ts.getTime())) return null;
            const dStr = formatDate(ts, { day: '2-digit', month: '2-digit', year: 'numeric' });
            const tStr = formatTime(ts, { hour: '2-digit', minute: '2-digit' });
            const c = clients.find((c) => c.id === clientId);
            // For a locked client (opened from the detail view) the row is
            // not in the audience-filtered `clients` list, so fall back to
            // the label passed in — keeps the preview name in sync with the SMS.
            const clientLabel = lockedClient
              ? lockedClient.label
              : c
                ? [c.title, c.first_name, c.last_name].filter(Boolean).join(' ')
                : '';
            const a = agents.find((a) => a.id === agentProfileId) ?? null;
            const agentLabel = a?.full_name ?? profile?.full_name ?? '';
            return (
              <div className="rounded-lg border border-[hsl(var(--brand-gold)/0.3)] bg-[hsl(var(--brand-gold)/0.06)] px-3 py-2.5 space-y-1.5">
                <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--brand-gold-deep))] font-semibold">
                  {t('appointments.create.sms_preview_label', 'SMS-Vorschau (Klient)')}
                </p>
                <p className="text-xs text-ink leading-relaxed">
                  4M Immobilien: Ihr Termin am <b>{dStr}</b> um <b>{tStr}</b> mit{' '}
                  <b>{agentLabel}</b> ist bestätigt.{clientLabel && ` (${clientLabel})`}
                </p>
                <p className="text-[10px] text-ink-3">
                  {t(
                    'appointments.create.sms_preview_hint',
                    'Datum und Uhrzeit prüfen — der SMS wird genau so versendet.',
                  )}
                </p>
              </div>
            );
          })()}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Abbrechen')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending
                ? t('appointments.create.submitting', 'Wird erstellt …')
                : t('appointments.create.submit', 'Anlegen')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
